import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import { greyMui, orangeMui, rydeColors } from "@ryde/theme";

const HUB_INFO_COLUMNS = "minmax(120px, 0.7fr) minmax(0, 2.3fr)";

export const HubStationsInfoContentStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "hub-stations-info-content",
  },
)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px 18px;

  .hub-info-loading,
  .hub-info-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 160px;
    padding: 24px;
    color: ${greyMui._600};
    font-size: 14px;
  }

  .hub-info-empty--compact {
    min-height: 80px;
    padding: 16px;
  }

  .hub-info-search {
    padding: 0 16px 4px;

    .MuiTextField-root {
      width: 100%;
    }

    .MuiInput-root {
      &::before {
        border-bottom-color: ${greyMui._300};
      }

      &:hover:not(.Mui-disabled, .Mui-error)::before {
        border-bottom-color: ${greyMui._400};
      }

      &::after {
        border-bottom-color: ${rydeColors.steel};
      }
    }

    .MuiInputAdornment-root,
    .MuiInputBase-input::placeholder {
      color: ${greyMui._500};
      opacity: 1;
    }
  }

  .hub-info-columns-header,
  .hub-info-row {
    display: grid;
    grid-template-columns: ${HUB_INFO_COLUMNS};
    align-items: start;
    column-gap: 16px;
    width: 100%;
  }

  .hub-info-columns-header {
    padding: 0 16px;
    color: ${greyMui._600};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;

    .hub-info-col-route {
      display: grid;
      grid-template-columns: 52px minmax(0, 1fr);
      column-gap: 8px;
      align-items: center;
      padding-inline-start: 10px;
    }
  }

  .hub-info-col-dir {
    white-space: nowrap;
  }

  .hub-info-row {
    padding: 14px 16px;
    border: 1px solid ${rydeColors.lightGreyBorder};
    border-radius: 12px;
    background: ${rydeColors.white};
    box-shadow: 0 1px 2px rgba(35, 55, 64, 0.04);
  }

  .hub-info-hub {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .hub-info-hub-name {
    margin: 0;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    font-weight: 650;
    font-size: 15px;
    line-height: 1.35;
    color: ${rydeColors.steel};
    text-align: start;
    word-break: break-word;

    &--clickable {
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .hub-info-hub-area {
    margin: 0;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    font-size: 11px;
    line-height: 1.3;
    font-weight: 500;
    color: ${greyMui._500};
    text-align: start;
    word-break: break-word;

    &--clickable {
      cursor: pointer;

      &:hover {
        color: ${rydeColors.steel};
        text-decoration: underline;
      }
    }
  }

  .hub-info-pairs {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    padding-inline-start: 10px;
  }

  .hub-info-route {
    display: grid;
    grid-template-columns: 52px minmax(0, 1fr);
    column-gap: 8px;
    align-items: start;
    min-width: 0;
  }

  .hub-info-direction-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 24px;
    margin-top: 3px;
    padding: 2px 6px;
    border-radius: 6px;
    background: ${greyMui._100};
    color: ${greyMui._700};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
    line-height: 1;
    white-space: nowrap;
  }

  .hub-info-route-stations {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .hub-info-route-sep {
    display: inline-flex;
    align-items: center;
    color: ${greyMui._500};
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
  }

  .hub-info-station-badge {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    margin: 0;
    padding: 5px 8px;
    border: 0;
    border-radius: 8px;
    background: ${rydeColors.smoke};
    color: ${greyMui._700};
    font: inherit;
    text-align: start;

    &--hub {
      color: ${orangeMui._800};

      .hub-info-station-name {
        color: inherit;
      }
    }

    &--clickable {
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .hub-info-station-name {
    font-size: 12.5px;
    line-height: 1.3;
    font-weight: 600;
    word-break: break-word;
  }
`;

export const HubStationRouteTooltipContent = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "hub-station-route-tooltip",
  },
)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 260px;

  .hub-info-station-tooltip__name {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
  }

  .hub-info-station-tooltip__area {
    font-size: 12px;
    font-weight: 400;
    line-height: 1.3;
    opacity: 0.85;
  }
`;
